import React, { useContext, useEffect, useMemo, useState } from 'react';
import './style.scss';
import _ from 'lodash';
import RouteUtils from 'services/RouteUtils';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import PatientUtils from 'services/PatientUtils';
import ProgressMessage from 'components/Utils/ProgressMessage';
import ChatBubble from 'components/Utils/ChatBubble';
import { STATUS, StudentConsentBadge } from 'scenes/HomeStaff/ClinicQueue/QueueBadge';
import ChatContext from 'contexts/ChatContext';
import useFetchPatients from 'hooks/useFetchPatients';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { usePathParams } from 'components/Navigation/NavStack';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { AssistantUnreadTotal, GetStaff, InternOf, IsAdmin } from 'careand-redux/selectors';
import ChatBubbleUtils from 'services/ChatBubbleUtils';
import assistantIcon from 'assets/img/icons/avatar-ai.svg';
import DefaultModal from 'components/Utils/DefaultModal';
import NotesBadge from './NotesBadge';
import { api } from 'careand-redux/api/ApiThunks';
import PerformanceUtils from 'services/PerformanceUtils';

const ChatBubbles = ({ onOpenAssistant, onOpen }) => {
  const dispatch = useAppDispatch();

  const patients = useAppSelector((state) => state.patients.byId);
  const activeBubble = useAppSelector((state) => state.ui.activeBubble);
  const allBubbles = useAppSelector((state) => state.ui.bubbles);
  const authentication = useAppSelector((state) => state.authentication);
  const queue = useAppSelector((state) => state.queue.elements);
  const internOf = useAppSelector(InternOf);
  const ownUser = useAppSelector(GetStaff);
  const activeAppointments = useAppSelector((state) => state.appointments.activeAppointments);
  const helpdeskMessages = useAppSelector((state) => state.resource.helpdeskMessages);
  const isAdmin = useAppSelector(IsAdmin);

  const ownId = authentication.user.id;

  const { joinedRooms, unreadMessages } = useContext(ChatContext);

  useFetchPatients(allBubbles);

  const chatBubbles = useMemo(() => {
    let inRoomPatientIds = queue
      .filter(
        ({ physician_id, status }) =>
          (physician_id === ownId || physician_id === internOf) &&
          (status === STATUS.called_in || status === STATUS.waiting || status === STATUS.help),
      )
      .map((element) => element.patient_id);

    return ChatBubbleUtils.getAllChatBubbles(joinedRooms, activeAppointments, inRoomPatientIds);
  }, [internOf, ownId, activeAppointments, queue, joinedRooms]);

  const helpdeskUnreadCount = useMemo(() => {
    let result = {};
    helpdeskMessages.forEach((message) => {
      let total = 0;
      (message?.messages || []).forEach(({ read_by = [] }) => {
        if (!read_by.includes(ownId)) total += 1;
      });
      result[message.id] = total;
    });
    return result;
  }, [ownId, helpdeskMessages]);

  const messageBubbles = useMemo(() => {
    return helpdeskMessages
      .filter(({ staff_id, archived }) => staff_id === ownId && !archived)
      .map((message) => {
        return {
          id: message.id,
          patient_id: message.patient_id,
          from: message.from,
          helpdesk: true,
        };
      });
  }, [ownId, helpdeskMessages]);

  const pinnedBubbles = useMemo(() => {
    const pinnedPatientIds = [
      ...(ownUser?.pinned_bubbles?.map((bubble) => bubble.patient_id) || []),
    ];

    const chats = _(chatBubbles)
      .filter((it) => pinnedPatientIds.includes(it.patient_id))
      .groupBy('patient_id')
      .mapValues((it) => ({
        room: it[0].room,
        room_name: it[0].id,
        isLiveChat: it[0].isLiveChat,
      }))
      .value();

    return pinnedPatientIds.map((patient_id) => {
      return {
        id: patient_id,
        patient_id,
        manual: true,
        ...(chats[patient_id] || {}),
      };
    });
  }, [ownUser, chatBubbles]);

  useEffect(() => {
    let allBubbles = [...chatBubbles, ...messageBubbles, ...pinnedBubbles];
    dispatch(uiActions.setBubbles(allBubbles));
  }, [chatBubbles, messageBubbles, pinnedBubbles, dispatch]);

  return (
    <ul className="chat-list">
      {isAdmin && <AssistantBubble isAdmin={isAdmin} onClick={onOpen} />}
      {[...chatBubbles, ...pinnedBubbles].map((bubble) => {
        let unreadCount = unreadMessages[bubble.id] || 0;
        return (
          <ChatBubbleWrapper
            key={bubble.id}
            bubble={bubble}
            activeBubble={activeBubble}
            patient={patients[bubble.patient_id]}
            unreadCount={unreadCount}
            isAdmin={isAdmin}
            onClick={onOpen}
          />
        );
      })}
      {messageBubbles.map((bubble) => (
        <HelpdeskBubbleWrapper
          key={bubble.id}
          bubble={bubble}
          activeBubble={activeBubble}
          patient={patients[bubble.patient_id]}
          unreadCount={helpdeskUnreadCount[bubble.id]}
          onClick={onOpen}
        />
      ))}
    </ul>
  );
};

const HelpdeskBubbleWrapper = ({ bubble, activeBubble, patient, unreadCount }) => {
  const dispatch = useAppDispatch();

  function onClickBubble() {
    dispatch(uiActions.setActiveBubble(bubble));
    dispatch(uiActions.showAssistant(undefined));
  }

  let title = patient ? PatientUtils.getName(patient) : bubble.from;
  let name = patient ? undefined : bubble.from;

  return (
    <DefaultTooltip key={bubble.id} placement="bottom" title={title}>
      <li key={bubble.id}>
        <ChatBubble
          name={name}
          onClick={onClickBubble}
          user={patient}
          selected={(activeBubble || {}).id === bubble.id}
          icons={['envelope']}
          unreadCount={unreadCount}
        />
      </li>
    </DefaultTooltip>
  );
};

export const AssistantBubble = ({ isAdmin, onClick }) => {
  const title = 'Carey';
  const dispatch = useAppDispatch();
  const openAssistant = useAppSelector((state) => state.ui.openAssistantId);
  const assistant = useAppSelector((state) => state.ui.assistant);
  const assistants = useAppSelector((state) => state.ui.assistants);
  const assistantUnread = useAppSelector(AssistantUnreadTotal);

  const anyChatLoading = useMemo(() => {
    if (openAssistant) return false;
    return assistants.find((it) => it.loading);
  }, [openAssistant, assistants]);

  function onClickBubble() {
    dispatch(uiActions.quickOpenAssistant());
    if (isAdmin) dispatch(uiActions.setActiveBubble(undefined));
    onClick?.();
  }

  return (
    <DefaultTooltip placement="bottom" title={title}>
      <li>
        <ChatBubble
          className="assistant"
          selected={assistant || openAssistant}
          defaultPhoto={assistantIcon}
          name={title}
          loading={anyChatLoading}
          onClick={onClickBubble}
          user={undefined}
          unreadCount={assistantUnread}
          badgeColor="#2693d1"
        />
      </li>
    </DefaultTooltip>
  );
};

const ChatBubbleWrapper = ({ bubble, activeBubble, patient, unreadCount, isAdmin, onClick }) => {
  const nav = useNavStack(Stacks.Master);
  const dispatch = useAppDispatch();
  const params = usePathParams();
  const activeBubbleId = activeBubble?.id || params.bubbleId;
  const [extraTooltip, setExtraTooltip] = useState('');
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const queue = useAppSelector((state) => state.queue.elements);
  const ownId = useAppSelector((state) => state.authentication.activeUserId);
  const ownPhysician = useAppSelector((state) => state.physicians.byId[ownId]);

  const queueElement = useMemo(() => {
    return queue?.find(({ patient_id }) => patient_id === patient?.id);
  }, [patient, queue]);

  function onClickBubble() {
    if (isAdmin) {
      dispatch(uiActions.setActiveBubble(bubble));
      dispatch(uiActions.showAssistant(undefined));
    } else {
      const route = RouteUtils.getRoute(RouteUtils.routes().bubble, {
        ':bubbleId': bubble.id,
      });
      nav.push(route);
    }
    onClick?.();
  }

  function handleContextMenu(e) {
    if (patient) {
      e.preventDefault();
      e.stopPropagation();
      setNotesModalOpen(true);
    }
  }

  const pinnedBubble = useMemo(() => {
    return ownPhysician?.pinned_bubbles?.find((it) => it.patient_id === patient?.id);
  }, [ownPhysician, patient]);

  const handleSave = async (notes) => {
    const appointment = bubble.appointment;
    if (appointment) {
      await dispatch(
        api.Appointments.updateAppointment({
          appointment: {
            ...appointment,
            notes: notes.trim(),
          },
        }),
      );
    } else if (ownPhysician) {
      const pinned_bubbles = PerformanceUtils.clone(ownPhysician.pinned_bubbles || []).map(
        (bubble) => {
          if (bubble.patient_id === patient.id) {
            return { ...bubble, notes: notes.trim() };
          }
          return bubble;
        },
      );
      if (!pinnedBubble) {
        pinned_bubbles.push({ patient_id: patient.id, notes: notes.trim() });
      }
      const physician = { ...ownPhysician, pinned_bubbles };
      await dispatch(api.Physicians.updatePhysician({ id: ownPhysician.id, physician }));
    }
  };

  let icons = [];

  if (bubble.type === 'booking') icons.push('calendar-clock');
  else if (bubble.chat) icons.push('message');

  const notes = (bubble.appointment || pinnedBubble)?.notes;

  const isSelected = activeBubbleId === bubble.id;

  if (patient !== undefined) {
    return (
      <li
        key={bubble.id}
        id={bubble.id}
        style={{ position: 'relative' }}
        onContextMenu={handleContextMenu}
      >
        <ChatBubble
          onClick={onClickBubble}
          user={patient}
          selected={isSelected}
          icons={icons}
          unreadCount={isSelected ? 0 : unreadCount}
          patientInRoom={bubble.patient_in_room}
          appointment={bubble.appointment}
          notes={notes}
          bubbleId={bubble.id}
          showTooltip={!isAdmin}
          extraTooltip={extraTooltip}
        />
        {ownPhysician && (
          <>
            <NotesBadge
              patient={patient}
              notesModalOpen={notesModalOpen}
              setNotesModalOpen={setNotesModalOpen}
              onNotesChanged={handleSave}
              notes={notes}
            />
            <StudentConsentBadge
              questionnaire={bubble.appointment?.questionnaire}
              element={queueElement}
              showTooltip={false}
              onDescription={(text) => setExtraTooltip(text)}
            />
          </>
        )}
      </li>
    );
  } else
    return (
      <li key={bubble.id} style={{ padding: '0 5px' }}>
        <ProgressMessage size={35} />
      </li>
    );
};

export const ChatBubblesModal = ({ isOpen, onRequestClose }) => {
  const isAdmin = useAppSelector(IsAdmin);
  return (
    isOpen && (
      <DefaultModal
        className="chat-bubbles-modal"
        wrap={true}
        padding={false}
        content={<ChatBubbles onOpen={onRequestClose} />}
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        title={isAdmin ? 'Open Helpdesk Messages' : 'Open Bubbles'}
      />
    )
  );
};

export default ChatBubbles;
